import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    checkUrl: String,
    uncheckUrl: String,
    frameId: String,
    guardedLinkSelector: String,
    requestParamName: { type: String, default: "checked_candidate" },
    responseIdsKey: { type: String, default: "checked_ids" }
  }

  static targets = [
    "rowCheckbox",
    "selectAll",
    "selectionField",
    "actionButton",
    "actionLabel"
  ]

  connect() {
    if (this.hasGuardedLinkSelectorValue) {
      this.boundGuardedLinkClick = this.handleGuardedLinkClick.bind(this)
      document.addEventListener("click", this.boundGuardedLinkClick)
    }

    this.restoreSelection()
  }

  disconnect() {
    if (this.boundGuardedLinkClick) {
      document.removeEventListener("click", this.boundGuardedLinkClick)
    }
  }

  toggleRowSelection(event) {
    this.persistSelectionChange(event.currentTarget.value, event.currentTarget.checked)
  }

  toggleSelectAll(event) {
    this.enabledRowCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = event.currentTarget.checked
    })

    this.persistSelectionChange(
      this.enabledRowCheckboxTargets.map((checkbox) => checkbox.value).join(","),
      event.currentTarget.checked
    )
  }

  restoreSelection(event) {
    if (this.shouldIgnoreRestoreEvent(event)) return

    this.syncSelection(this.selectedIds())
  }

  clearSelection() {
    this.syncSelection([])
  }

  handleGuardedLinkClick(event) {
    const link = event.target.closest(this.guardedLinkSelectorValue)

    if (!link || this.selectedIds().length > 0) return

    event.preventDefault()
  }

  syncSelection(ids) {
    const normalizedIds = this.normalizeIds(ids)
    this.selectionFieldTarget.value = normalizedIds.join(",")

    this.enabledRowCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = normalizedIds.includes(checkbox.value)
    })

    this.updateActionState(normalizedIds.length)
    this.updateSelectAllState()

    return normalizedIds
  }

  selectedIds() {
    return this.normalizeIds(this.selectionFieldTarget.value.split(","))
  }

  updateActionState(selectedCount) {
    const hasSelection = selectedCount > 0
    this.actionButtonTargets.forEach((button) => {
      button.disabled = !hasSelection
    })

    this.updateActionLabel(selectedCount)
  }

  updateActionLabel(count) {
    this.actionLabelTargets.forEach((label) => {
      const baseLabel = label.dataset.baseLabel
      label.textContent = count > 0 ? `${baseLabel} (${count})` : baseLabel
    })
  }

  updateSelectAllState() {
    const allChecked =
      this.enabledRowCheckboxTargets.length > 0 &&
      this.enabledRowCheckboxTargets.every((checkbox) => checkbox.checked)

    this.selectAllTargets.forEach((checkbox) => {
      checkbox.checked = allChecked
    })
  }

  persistSelectionChange(value, checked) {
    const url = checked ? this.checkUrlValue : this.uncheckUrlValue
    if (!url || !value) return

    window.$.post(url, { [this.requestParamNameValue]: value }, (result) => {
      this.syncSelection(this.responseIds(result))
    }).fail(() => {
      this.restoreSelection()
    })
  }

  responseIds(result) {
    return this.normalizeIds(String(result?.[this.responseIdsKeyValue] || "").split(","))
  }

  shouldIgnoreRestoreEvent(event) {
    return Boolean(
      event &&
      event.type === "turbo:frame-load" &&
      this.hasFrameIdValue &&
      event.target.id !== this.frameIdValue
    )
  }

  normalizeIds(ids) {
    return (ids || []).reduce((normalizedIds, id) => {
      const normalizedId = String(id || "").trim()

      if (normalizedId !== "" && !normalizedIds.includes(normalizedId)) {
        normalizedIds.push(normalizedId)
      }

      return normalizedIds
    }, [])
  }

  get enabledRowCheckboxTargets() {
    return this.rowCheckboxTargets.filter((checkbox) => !checkbox.disabled)
  }
}
